import React, { useState } from 'react';

const WhatsNew: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'release' | 'guide' | 'roadmap' | 'founder'>('release');

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            What's New at{' '}
            <span className="bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent">
              Svarra
            </span>
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Discover the latest features, updates, and our vision for the future
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-1 shadow-sm">
            <button
              onClick={() => setActiveTab('release')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeTab === 'release'
                  ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-md'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                Latest Release
              </div>
            </button>
            <button
              onClick={() => setActiveTab('guide')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeTab === 'guide'
                  ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-md'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Quick Start
              </div>
            </button>
            <button
              onClick={() => setActiveTab('roadmap')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeTab === 'roadmap'
                  ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-md'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                </svg>
                Roadmap
              </div>
            </button>
            <button
              onClick={() => setActiveTab('founder')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeTab === 'founder'
                  ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white shadow-md'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Founder's Note
              </div>
            </button>
          </div>
        </div>

        {/* Tab Content */}
        <div className="animate-fadeIn">
          {/* Latest Release Tab */}
          {activeTab === 'release' && (
            <div className="max-w-5xl mx-auto">
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl overflow-hidden">
                {/* Header with gradient */}
                <div className="bg-gradient-to-r from-primary-500 to-secondary-500 p-8">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm text-white text-sm font-semibold rounded-full">
                      BETA LAUNCH
                    </span>
                    <span className="text-sm text-white/80">October 2025</span>
                  </div>
                  <h2 className="text-3xl font-bold text-white mb-3">
                    Welcome to Svarra Beta! 🚀
                  </h2>
                  <p className="text-white/90 text-lg">
                    Join the first wave of businesses automating customer interactions with AI
                  </p>
                </div>

                <div className="p-8">
                  {/* What Beta Means */}
                  <div className="mb-8 p-6 bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500 rounded-r-xl">
                    <h3 className="font-bold text-blue-900 dark:text-blue-300 mb-2 text-lg">What Beta Means</h3>
                    <p className="text-blue-800 dark:text-blue-400">
                      You're among the first to experience Svarra! We're actively gathering feedback, fixing bugs, and adding features based on real user needs.
                      Your input directly shapes the product, and you get exclusive early access while we fine-tune everything.
                    </p>
                  </div>

                  {/* Features Grid */}
                  <h3 className="font-bold text-gray-900 dark:text-white mb-6 text-xl">Available Features</h3>
                  <div className="grid md:grid-cols-2 gap-4 mb-8">
                    {[
                      {
                        title: 'AI-Powered Agents',
                        desc: 'Create intelligent agents that understand your business 24/7',
                        icon: '🤖'
                      },
                      {
                        title: 'Auto Booking',
                        desc: 'Let customers book appointments directly through chat',
                        icon: '📅'
                      },
                      {
                        title: 'Google Calendar',
                        desc: 'Seamlessly sync bookings with your calendar',
                        icon: '🔄'
                      },
                      {
                        title: 'Multi-Agent Support',
                        desc: 'Manage up to 5 specialized agents for different needs',
                        icon: '👥'
                      },
                      {
                        title: 'Real-Time Analytics',
                        desc: 'Track conversations, bookings, and engagement',
                        icon: '📊'
                      },
                      {
                        title: 'Smart Notifications',
                        desc: 'Automated email confirmations (SMS coming soon!)',
                        icon: '📧'
                      },
                      {
                        title: 'Knowledge Base',
                        desc: 'Upload documents so your agent knows how to help',
                        icon: '📚'
                      },
                      {
                        title: 'Custom Branding',
                        desc: 'Match your agent to your business personality',
                        icon: '🎨'
                      }
                    ].map((feature, idx) => (
                      <div
                        key={idx}
                        className="group p-5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all duration-200"
                      >
                        <div className="flex items-start gap-3">
                          <span className="text-3xl">{feature.icon}</span>
                          <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-1">{feature.title}</h4>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{feature.desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Beta Perks Banner */}
                  <div className="relative overflow-hidden p-6 bg-gradient-to-br from-primary-50 via-secondary-50 to-primary-50 dark:from-primary-900/20 dark:via-secondary-900/20 dark:to-primary-900/20 border-2 border-primary-200 dark:border-primary-800 rounded-xl">
                    <div className="relative z-10">
                      <h3 className="font-bold text-primary-900 dark:text-primary-300 mb-3 text-xl flex items-center gap-2">
                        <span>🎉</span> Beta User Perks
                      </h3>
                      <ul className="space-y-2 text-primary-800 dark:text-primary-400">
                        <li className="flex items-center gap-2">
                          <span className="text-primary-500">✓</span>
                          <strong>Completely FREE until December 30th, 2025</strong>
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-primary-500">✓</span>
                          Exclusive early access to all new features
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-primary-500">✓</span>
                          Direct line to our team for feedback and support
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-primary-500">✓</span>
                          Your input shapes the product roadmap
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* CTA */}
                  <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700 text-center">
                    <p className="text-gray-600 dark:text-gray-400 mb-4 text-lg">
                      Ready to automate your customer interactions?
                    </p>
                    <a
                      href="/register"
                      className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold rounded-xl hover:shadow-2xl hover:scale-105 transition-all duration-200"
                    >
                      Get Started Free
                      <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quick Start Guide Tab */}
          {activeTab === 'guide' && (
            <div className="max-w-5xl mx-auto">
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl p-8">
                <div className="text-center mb-10">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
                    Quick Start Guide
                  </h2>
                  <p className="text-lg text-gray-600 dark:text-gray-400">
                    Get your AI agent live in under 5 minutes
                  </p>
                </div>

                {/* Timeline Steps */}
                <div className="relative">
                  {/* Vertical Line */}
                  <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-500 to-secondary-500"></div>

                  {/* Steps */}
                  <div className="space-y-8">
                    {[
                      {
                        num: 1,
                        title: 'Create Your First Agent',
                        desc: 'After logging in, click "Create New Agent" on your dashboard. Give your AI agent a name (e.g., "Acme Salon Assistant").',
                        tip: 'You can create up to 5 different agents for different businesses or brands.'
                      },
                      {
                        num: 2,
                        title: 'Configure Essential Settings',
                        desc: 'Navigate to your agent\'s Settings page and configure:',
                        items: [
                          'Business Hours: Set your operating hours',
                          'Services: Add services with pricing and durations',
                          'Business Description: Tell your AI about your business',
                          'Tone: Choose how your AI speaks'
                        ],
                        tip: 'During agent creation, you can upload a PDF menu/brochure to auto-extract services and hours using AI!'
                      },
                      {
                        num: 3,
                        title: 'Connect Your Google Calendar',
                        badge: 'Required for Bookings',
                        desc: 'Go to Settings → Calendar Integration and connect your Google Calendar. This is required for appointment booking to work.',
                        items: [
                          'Check real-time availability',
                          'Book appointments automatically',
                          'Send confirmation emails',
                          'Sync bookings to your calendar'
                        ],
                        tip: 'Google Calendar is currently the only supported calendar. Additional integrations coming soon!'
                      },
                      {
                        num: 4,
                        title: 'Share Your Chat Link',
                        desc: 'From your Dashboard, click "Copy Link" next to your agent. Share this link on your website, email signature, social media, or via QR code.',
                        tip: 'You can preview and test your chatbot anytime by clicking "View Chat".'
                      }
                    ].map((step) => (
                      <div key={step.num} className="relative pl-20">
                        {/* Number Circle */}
                        <div className="absolute left-0 w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-bold text-2xl shadow-lg">
                          {step.num}
                        </div>

                        {/* Content */}
                        <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                              {step.title}
                            </h3>
                            {step.badge && (
                              <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-semibold rounded">
                                {step.badge}
                              </span>
                            )}
                          </div>
                          <p className="text-gray-600 dark:text-gray-400 mb-3">{step.desc}</p>
                          {step.items && (
                            <ul className="space-y-2 mb-3 ml-4">
                              {step.items.map((item, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-gray-600 dark:text-gray-400">
                                  <span className="text-primary-500 mt-1">•</span>
                                  <span>{item}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                            <p className="text-sm text-blue-800 dark:text-blue-300">
                              💡 <strong>Tip:</strong> {step.tip}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Additional Info */}
                <div className="grid md:grid-cols-2 gap-6 mt-12">
                  <div className="p-6 bg-green-50 dark:bg-green-900/20 border-l-4 border-green-500 rounded-r-xl">
                    <h4 className="font-bold text-green-900 dark:text-green-300 mb-3 text-lg">📊 Track Your Success</h4>
                    <ul className="space-y-2 text-sm text-green-800 dark:text-green-400">
                      <li><strong>Bookings:</strong> View all appointment requests</li>
                      <li><strong>Analytics:</strong> See conversations, services, peak times</li>
                    </ul>
                  </div>

                  <div className="p-6 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-500 rounded-r-xl">
                    <h4 className="font-bold text-yellow-900 dark:text-yellow-300 mb-3 text-lg">⚙️ Important Settings</h4>
                    <ul className="space-y-2 text-sm text-yellow-800 dark:text-yellow-400">
                      <li><strong>Booking Approval:</strong> Manual or auto-confirm</li>
                      <li><strong>Email Notifications:</strong> Get notified (SMS coming soon!)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Roadmap Tab */}
          {activeTab === 'roadmap' && (
            <div className="max-w-5xl mx-auto">
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl p-8">
                <div className="text-center mb-10">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-pink-500 to-purple-500 rounded-2xl mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
                    Product Roadmap
                  </h2>
                  <p className="text-lg text-gray-600 dark:text-gray-400">
                    What's coming next to Svarra AI
                  </p>
                </div>

                {/* Roadmap Items */}
                <div className="space-y-6">
                  {/* Multi-Channel Communication */}
                  <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Multi-Channel Communication</h3>
                        <p className="text-gray-700 dark:text-gray-300 mb-3">Expand beyond web chat to reach customers wherever they are</p>
                        <ul className="space-y-2 text-sm">
                          <li className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                            <strong>SMS Messaging:</strong> Send and receive text messages with customers
                          </li>
                          <li className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                            <strong>Instagram DMs:</strong> Automatically respond to Instagram direct messages
                          </li>
                          <li className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                            <strong>Facebook Messenger:</strong> Handle customer inquiries on Facebook
                          </li>
                        </ul>
                      </div>
                      <div className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs font-semibold rounded-full">
                        Q1 2026
                      </div>
                    </div>
                  </div>

                  {/* Voice & Phone */}
                  <div className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Voice & Phone Integration</h3>
                        <p className="text-gray-700 dark:text-gray-300 mb-3">Let Svarra handle phone calls with natural voice AI</p>
                        <ul className="space-y-2 text-sm">
                          <li className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                            <strong>Inbound Calls:</strong> Answer customer calls automatically 24/7
                          </li>
                          <li className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                            <strong>Natural Conversations:</strong> Human-like voice interactions
                          </li>
                          <li className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                            <strong>Appointment Booking:</strong> Book appointments via phone call
                          </li>
                        </ul>
                      </div>
                      <div className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-semibold rounded-full">
                        Q2 2026
                      </div>
                    </div>
                  </div>

                  {/* Advanced Features */}
                  <div className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800 rounded-xl">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Enhanced Capabilities</h3>
                        <p className="text-gray-700 dark:text-gray-300 mb-3">More power and intelligence for your AI agent</p>
                        <ul className="space-y-2 text-sm">
                          <li className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                            <strong>Additional Calendar Integrations:</strong> Outlook, Apple Calendar support
                          </li>
                          <li className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                            <strong>Advanced Analytics:</strong> Deeper insights into customer behavior
                          </li>
                          <li className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                            <strong>Custom Integrations:</strong> Connect with your existing tools
                          </li>
                        </ul>
                      </div>
                      <div className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 text-xs font-semibold rounded-full">
                        Later
                      </div>
                    </div>
                  </div>
                </div>

                {/* Feedback CTA */}
                <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700 text-center">
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    <strong>Your feedback shapes our roadmap!</strong> Let us know which features you'd like to see first.
                  </p>
                  <a
                    href="/contact"
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-200"
                  >
                    Share Your Ideas
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Founder's Note Tab */}
          {activeTab === 'founder' && (
            <div className="max-w-5xl mx-auto">
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-xl p-8">
                <div className="text-center mb-10">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl mb-4">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                    </svg>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
                    From the Founder
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Updates and insights from our journey
                  </p>
                </div>

                <div className="prose prose-gray dark:prose-invert max-w-none">
                  <div className="p-8 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800 dark:to-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                      Welcome to Svarra
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                      We're building Svarra to help local businesses automate their customer interactions with AI.
                      This section will feature personal notes from our founders about our journey, learnings, and vision.
                    </p>
                    <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                      Check back soon for updates on our progress and insights from building Svarra.
                    </p>
                  </div>

                  <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-bold text-2xl shadow-lg">
                        S
                      </div>
                      <div>
                        <p className="font-bold text-gray-900 dark:text-white text-lg">
                          Svarra Team
                        </p>
                        <p className="text-gray-500 dark:text-gray-400">
                          Building the future of local business automation
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsNew;
